import React, { Fragment } from 'react';
import { Combobox, Transition } from '@headlessui/react';

interface IAsyncSelectCheckbox {
    options: Array<{ label: string; value: any }>;
    selectedOption?: any;
    onChange: (option: any) => void;
    onSearch: (search: string) => void;
    onLoadMore?: () => void;
    hasNextPage?: boolean;
    loading?: boolean;
    placeholder?: string;
    name?: string;
    error?: string;
}

const AsyncSelectCheckbox: React.FC<IAsyncSelectCheckbox> = ({
    options,
    selectedOption,
    onChange,
    onSearch,
    onLoadMore,
    hasNextPage = false,
    loading = false,
    placeholder = 'Select...',
    name,
    error,
}) => {
    return (
        <div className="w-auto">
            {name && (
                <label className="block mb-1 font-medium text-gray-500 dark:text-gray-300">
                    {name}
                </label>
            )}

            <Combobox value={selectedOption} onChange={onChange}>
                <div className="relative">
                    <div
                        className={`border ${error ? 'border-red-500' : 'border-gray-300'
                            } rounded-sm relative`}
                    >
                        <Combobox.Input
                            className="w-full p-2 pr-10 border-none focus:ring-0 bg-white dark:bg-black dark:text-white"
                            displayValue={(opt: any) => {
                                if (!opt && selectedOption) return '';
                                return opt?.label && selectedOption ? opt?.label : '';
                            }}
                            onChange={(e) => onSearch(e.target.value)}
                            placeholder={placeholder}
                        />

                        <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                            <svg
                                className="w-4 h-4 text-gray-500"
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 20 20"
                                fill="currentColor"
                            >
                                <path
                                    fillRule="evenodd"
                                    d="M5.23 7.21a.75.75 0 011.06.02L10 11.293l3.71-4.06a.75.75 0 111.08 1.04l-4.25 4.65a.75.75 0 01-1.08 0l-4.25-4.65a.75.75 0 01.02-1.06z"
                                    clipRule="evenodd"
                                />
                            </svg>
                        </Combobox.Button>
                    </div>

                    <Transition
                        as={Fragment}
                        leave="transition ease-in duration-100"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto bg-white dark:bg-black border rounded shadow-md">
                            {loading && (
                                <div className="py-2 px-4 text-center text-gray-500">Loading...</div>
                            )}

                            {!loading && options.length === 0 && (
                                <div className="py-2 px-4 text-center text-gray-500">
                                    No options found
                                </div>
                            )}

                            {!loading &&
                                options.map((option, idx) => (
                                    <Combobox.Option
                                        key={idx}
                                        value={option}
                                        className="cursor-pointer px-4 py-2 hover:bg-blue-100 dark:hover:bg-gray-700 text-gray-800 dark:text-white"
                                    >
                                        {option.label}
                                    </Combobox.Option>
                                ))}

                            {!loading && hasNextPage && onLoadMore && (
                                <div className="text-center p-2">
                                    <button
                                        type="button"
                                        className="text-sm text-blue-600 hover:underline"
                                        onClick={onLoadMore}
                                    >
                                        Show More
                                    </button>
                                </div>
                            )}
                        </Combobox.Options>
                    </Transition>
                </div>

                {error && <p className="text-sm text-red-500 mt-1">{error}</p>}
            </Combobox>
        </div>
    );
};

export default AsyncSelectCheckbox;